import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getAllRequests } from '../store/slices/Requestor/employeeInfoSlice'
import { AppDispatch, RootState } from '../store/store'

/** History tab default period ("Last 90 days"); must match the dropdown default. */
export const DEFAULT_HISTORY_TIME_PERIOD = 3

/**
 * Tab configuration for initial data fetching
 */
export interface TabFetchConfig {
  type?: string
  page?: number
  perPage?: number
  sortColumn?: 'fromDate' | 'hoursRequested'
  [key: string]: any
}

/**
 * @param tabConfigs - Per-tab fetch params.
 * @param refetchKey - When this value changes, the tabs are refetched again
 * instead of only on initial mount. Used by the Requestor tabs so switching
 * the active Manual Time Off employee (or returning to the logged-in user's
 * own context) refreshes the lists instead of leaving the previous
 * employee's data on screen — the route itself doesn't remount this
 * component, so a mount-only fetch would otherwise never notice the change.
 * Approver tabs have no such context and pass a constant, preserving the
 * original fetch-once-on-mount behavior exactly.
 */
export const useFetchAllTabsOnLoad = (
  tabConfigs: TabFetchConfig[],
  refetchKey: string = ''
) => {
  const dispatch = useDispatch<AppDispatch>()

  // Get persisted sort orders from Redux
  const timeOffRequestsSortOrder = useSelector(
    (state: RootState) => state.employeeInfo.timeOffRequests.sortOrder
  )
  const historySortOrder = useSelector(
    (state: RootState) => state.employeeInfo.timeOffHistory.sortOrder
  )
  const vacationBiddingSortOrder = useSelector(
    (state: RootState) => state.employeeInfo.vacationBidding.sortOrder
  )

  useEffect(() => {
    const now = new Date()
    const baseParams = {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
      page: 1,
      perPage: 20,
      sortColumn: 'fromDate' as const,
    }

    // Fetch all configured tabs with persisted sort orders
    const sortOrders = [
      timeOffRequestsSortOrder, // Time Off Requests
      historySortOrder, // History
      vacationBiddingSortOrder, // Vacation Bidding
    ]

    // Aborting these on cleanup prevents an out-of-order response for a
    // PREVIOUSLY active employee/context from overwriting the currently
    // active one's lists — the reducer has no staleness check, so whichever
    // response arrives last wins regardless of which request was dispatched
    // more recently.
    const promises = tabConfigs.map((config, index) =>
      dispatch(
        getAllRequests({
          ...baseParams,
          ...config,
          sortOrder: sortOrders[index],
          sortColumn: config.sortColumn ?? baseParams.sortColumn,
        })
      )
    )

    return () => {
      promises.forEach((promise) => promise?.abort?.())
    }
  }, [dispatch, refetchKey])
}

export const useFetchAllRequestorTabs = () => {
  const manualSelectedNuid = useSelector(
    (state: RootState) => state.manualTimeOff?.selectedEmployee?.nuid ?? ''
  )

  useFetchAllTabsOnLoad(
    [
      {}, // Time Off Requests (default - no type parameter)
      { type: 'HISTORY', timePeriod: DEFAULT_HISTORY_TIME_PERIOD }, // History
      { type: 'VACATION_BID' }, // Vacation Bidding
    ],
    manualSelectedNuid
  )
}

export const useFetchAllApproverTabs = () => {
  useFetchAllTabsOnLoad([
    { type: 'APPROVER' }, // Approver Requests
    { type: 'APPROVER_HISTORY' }, // Approver History
  ])
}
